/**
 * DevMode — admin only. Sub-tabs: Overview · Tasks · Bugs · Knowledge ·
 * Test on phone · Production, plus a Settings panel (AI keys) on the right.
 * The parent renders this only when `kiaan_dev_mode` is true; the server
 * answers 403 to everyone else on /api/kiaan/dev/*.
 */
import { useState } from 'react';
import { Overview } from './Overview';
import { Tasks } from './Tasks';
import { Bugs } from './Bugs';
import { Knowledge } from './Knowledge';
import { TestOnPhone } from './TestOnPhone';
import { Production } from './Production';
import { Settings } from './Settings';
import { KiaanBrowser } from './KiaanBrowser';
import { useAuthStore } from '../../../stores/auth';

export type DevTabKey = 'overview' | 'tasks' | 'bugs' | 'knowledge' | 'phone' | 'production' | 'operator' | 'settings';

const TABS: { key: DevTabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'bugs', label: 'Bugs' },
  { key: 'knowledge', label: 'Knowledge' },
  { key: 'phone', label: 'Test on phone' },
  { key: 'production', label: 'Production' },
];

/** Same rule as the backend (SUN-101): by role name, not by a grantable permission. */
const BROWSER_ROLES = ['admin', 'super_admin', 'owner'];

export function DevMode({ manageKeys, manageKnowledge }: { manageKeys: boolean; manageKnowledge: boolean }) {
  const roleName = useAuthStore((s: any) => s.user?.role?.name as string | undefined);
  const browserOk = !!roleName && BROWSER_ROLES.includes(roleName.toLowerCase());
  const [tab, setTab] = useState<DevTabKey>('overview');
  return (
    <div className="kw-body" data-testid="dev-mode">
      <div className="kw-tabs kw-sub" role="tablist" aria-label="Dev mode">
        {[...TABS, ...(browserOk ? [{ key: 'operator' as DevTabKey, label: 'Kiaan Browser 🔒' }] : [])].map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`kw-tab${tab === t.key ? ' kw-sub-on' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
        <span className="kw-sp" />
        <button type="button" role="tab" aria-selected={tab === 'settings'} className={`kw-tab${tab === 'settings' ? ' kw-sub-on' : ''}`} onClick={() => setTab('settings')} title="AI keys and branding">
          ⚙ Settings
        </button>
      </div>
      <div className="kw-body kw-scroll">
        {tab === 'overview' && <Overview onOpenBugs={() => setTab('bugs')} />}
        {tab === 'tasks' && <Tasks />}
        {tab === 'bugs' && <Bugs />}
        {tab === 'knowledge' && <Knowledge canManage={manageKnowledge} />}
        {tab === 'phone' && <TestOnPhone />}
        {tab === 'production' && <Production />}
        {tab === 'operator' && browserOk && <KiaanBrowser />}
        {tab === 'settings' && <Settings canManageKeys={manageKeys} />}
      </div>
    </div>
  );
}
